import Cookies from 'js-cookie';
import { string } from 'yup';

import { isDarkMode } from "./utils";

// Stripe rejects charges below 50 cents, its documented minimum.
const STRIPE_MINIMUM_CHARGE_CENTS = 50

// Convert a dollar amount to the integer-cent minor units Stripe expects, clamped up
// to Stripe's minimum charge.
function toStripeMinorUnits(amount) {
    return Math.max(Math.round((amount || 0) * 100), STRIPE_MINIMUM_CHARGE_CENTS)
}

// POST a JSON payload to a payment endpoint, attaching the CSRF token. Returns the parsed response
// body; on a non-2xx response throws an Error whose message is the first server-supplied reason,
// covering the keys the payment endpoints raise: payment_data (a declined card/bank), non_field_errors
// (state errors such as already-paid), and receipt_email (an invalid receipt address). The parsed body
// and status are attached to the error (responseData, status) so callers can route a field error -- e.g.
// a rejected receipt_email -- back onto its field instead of failing the whole flow.
async function postPaymentJson(endpoint, payload) {
    const headers = { 'Content-Type': 'application/json', 'X-CSRFToken': Cookies.get('csrftoken') }
    const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(payload) })
    let data
    try {
        data = await response.json()
    } catch (error) {
        data = {}
    }
    if (!response.ok) {
        const error = new Error(
            data.payment_data?.[0] || data.non_field_errors?.[0] || data.receipt_email?.[0] || data.error || `Request failed (${response.status})`
        )
        error.responseData = data
        error.status = response.status
        throw error
    }
    return data
}

// Dropdown label for a saved card returned by the list-payment-methods endpoint, e.g.
// "VISA ending in 4242 (expires 12/2027)". The expiry is omitted when either part is missing.
function formatSavedCardLabel(paymentMethod) {
    const brand = (paymentMethod.name || "Card").toUpperCase()
    const expirationMonth = paymentMethod.card_exp_month
    const expirationYear = paymentMethod.card_exp_year
    const expiration = (expirationMonth && expirationYear) ? ` (expires ${expirationMonth}/${expirationYear})` : ""
    return `${brand} ending in ${paymentMethod.last4}${expiration}`
}

// Dropdown label for a saved US bank account, e.g. "WELLS FARGO ••••6789 (checking)".
function formatSavedBankLabel(paymentMethod) {
    const accountType = paymentMethod.account_type ? ` (${paymentMethod.account_type})` : ""
    return `${paymentMethod.name || "Bank"} ••••${paymentMethod.last4}${accountType}`
}

// PaymentElement options for the INTERNAL (staff) card-on-file forms. Staff only enter a card to
// charge it: Link is a wallet, so it (and the other wallets) is disabled via the wallets hash, and
// the name/email fields are turned off because the receipt email is captured in a separate field.
const INTERNAL_CARD_PAYMENT_ELEMENT_OPTIONS = {
    layout: 'tabs',
    fields: { billingDetails: { name: 'never', email: 'never' } },
    wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
}

// PaymentElement options for the PUBLIC (client-facing) payment forms. Clients enter and confirm a
// brand-new card or bank account here. Email is collected by our own field up front (not the Element) so it
// can be set as the PaymentIntent's receipt_email at creation -- the only point a client-side-confirmed
// intent can carry one -- hence email: 'never' (we re-pass it at confirm via payment_method_data). name and
// address stay 'auto' (ACH needs the name), and wallets are left at Stripe's defaults.
const PUBLIC_PAYMENT_ELEMENT_OPTIONS = {
    layout: 'tabs',
    fields: { billingDetails: { email: 'never', name: 'auto', address: 'auto' } },
}

// yup's .email() accepts a dotless domain (test@test, a@b), which Django's validate_email rejects -- so
// require a dotted domain too, keeping the client in step with the server and never building an intent the
// create-payment-intent endpoint would 400 on.
const receiptEmailSchema = string().email().matches(/@[^\s@]+\.[^\s@]+$/).required()

function isValidReceiptEmail(value) {
    return receiptEmailSchema.isValidSync((value || '').trim())
}

// Absolute return_url for stripe.confirmPayment. Even with redirect: 'if_required', Stripe rejects the
// call without a return_url whenever the chosen method *could* need a redirect, so this is always sent.
// It must be the current page: a redirect-based method (e.g. an OAuth bank for ACH) sends the payer back
// here, where the mounted form's handleRedirectReturn completes the capture from the query params Stripe
// appends -- and window.location.href preserves the ?mode=pay-online that gates the form rendering on return.
function buildStripeReturnUrl() {
    return window.location.href
}

// Stripe Payment Element appearance. Colors and type mirror the legacy form inputs
// (.data-panel__form__field__input--stripe-elements); the design tokens are hardcoded as
// concrete hex/rem here because the Element renders in a cross-origin iframe that can't read
// the page's CSS variables.
function getStripeAppearance() {
    const isDark = isDarkMode()

    const colorText = isDark ? '#eeeeee' : '#202020'         // --color-text-base (gray-12)
    const colorBackground = isDark ? '#111111' : '#ffffff'   // --color-card-background
    const colorBorder = isDark ? '#606060' : '#bbbbbb'       // --color-input-border (gray-8)
    const colorBorderFocus = isDark ? '#6e6e6e' : '#8d8d8d'  // --color-input-border-focus (gray-9)
    const colorPlaceholder = isDark ? '#6e6e6e' : '#8d8d8d'  // --color-text-context-light (gray-9)
    const colorAccent = '#29a383'                            // --color-accent-primary (jade-9)
    const colorError = isDark ? '#ff977d' : '#d13415'        // --color-text-invalid (tomato-11)

    const fieldStyles = {
        color: colorText,
        backgroundColor: colorBackground,
        border: `1.5px solid ${colorBorder}`,  // --border-input-size
        borderRadius: '0.25rem',               // --border-radius-base
        fontFamily: '"Inter", sans-serif',     // --font-family-base
        fontSize: '1rem',                      // --font-size-base
        padding: '0.5rem 0.625rem',
    }

    return {
        // Base preset for the parts not targeted by a rule (loading spinner, mandate text).
        theme: isDark ? 'night' : 'stripe',
        rules: {
            '.Label': { display: 'none' },
            '.Input, .Block, .Tab': fieldStyles,
            '.Input::placeholder': { color: colorPlaceholder },
            '.Input:focus': { border: `1.5px solid ${colorBorderFocus}` },
            '.Input--invalid': { color: colorError },
            '.Tab:focus, .Tab--selected': {
                color: colorAccent,
                border: `1.5px solid ${colorAccent}`,
            },
            '.TabIcon--selected': { fill: colorAccent },
            '.Error': { color: colorError },
        },
    }
}

export { buildStripeReturnUrl, formatSavedBankLabel, formatSavedCardLabel, getStripeAppearance, INTERNAL_CARD_PAYMENT_ELEMENT_OPTIONS, isValidReceiptEmail, postPaymentJson, PUBLIC_PAYMENT_ELEMENT_OPTIONS, toStripeMinorUnits };
